var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { useEffect, useMemo, useState } from 'react';
import { useUnmount } from 'react-use';
import { analyticsApi } from 'apis/analytics';
import { analytics } from 'app/utils/segment';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { useAppPathParams } from 'utils/url/useAppPathParams';
import { analyticsActions } from '../../../../analytics.actions';
/**
 * A hook for fetching a quick view based on the dashboard slug and quick view
 * id in the URL path params. First the dashboard matching the slug is looked
 * up, then the quick view is fetched from the API with the matching dashboard
 * id and the quick view id from the URL. If the quick view exists, we overwrite
 * the existing parameters in the Redux state and store a snapshot of the
 * previous state, to which we return to when leaving the quick view.
 */
export const useQuickViewInit = () => {
    const dispatch = useAppDispatch();
    const params = useAppPathParams('/analytics/dashboard/:slug/quick-view/:id');
    const [asyncStatus, setAsyncStatus] = useState('uninitialized');
    const [readQuickViewQuery, { quickView }] = analyticsApi.useLazyReadQuickViewQuery({
        selectFromResult: (_a) => {
            var { data } = _a, rest = __rest(_a, ["data"]);
            return (Object.assign(Object.assign({}, rest), { quickView: data === null || data === void 0 ? void 0 : data.data.quick_view }));
        },
    });
    const { dashboard } = analyticsApi.endpoints.readDashboards.useQueryState(undefined, {
        selectFromResult: ({ data }) => ({
            dashboard: data === null || data === void 0 ? void 0 : data.data.dashboards.find((dashboard) => dashboard.slug === params.slug),
        }),
    });
    useEffect(() => {
        const readQuickView = () => __awaiter(void 0, void 0, void 0, function* () {
            if (dashboard) {
                setAsyncStatus('pending');
                const { data: { quick_view: quickView }, } = yield readQuickViewQuery({
                    dashboardId: dashboard.id,
                    id: params.id,
                }).unwrap();
                if (quickView) {
                    analytics.analyticsQuickViewViewed({
                        dashboard: dashboard.title,
                        shared: quickView.shared,
                    });
                    dispatch(analyticsActions.setQuickView({ dashboard, quickView }));
                    setAsyncStatus('fulfilled');
                }
            }
        });
        readQuickView().catch(() => {
            setAsyncStatus('rejected');
        });
        // Only run the effect when the URL path params change
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [params.id, params.slug]);
    // Restores the previously saved dashboard snapshot when leaving the quick view.
    useUnmount(() => {
        dispatch(analyticsActions.restoreSnapshot());
    });
    return useMemo(() => ({
        quickView,
        asyncStatus,
    }), [quickView, asyncStatus]);
};
